<script setup lang="ts">
import { computed, inject } from "vue";
import { useIsInPdfMode } from "~/stores/pdfModeStore";
import {
  FormResponseState,
  formResponseInjectKey,
} from "~/NewFormBuilder/composables/useFormResponse";

import {
  FormElementType,
  RadioButtonsFormElement,
  RadioButtonsDisplayFormat,
} from "~/api";
import { DefaultFormComponentProps } from "./FormComponentRenderer.vue";

const props = defineProps<DefaultFormComponentProps<RadioButtonsFormElement>>();

const { useAnswer } = inject(
  formResponseInjectKey,
  // Cast the type since we know it is always provided. For more info see:
  // https://vuejs.org/guide/typescript/composition-api.html#typing-provide-inject
) as FormResponseState;
// Array of indices of the selected checkbox items
const answer = useAnswer<FormElementType.RADIO_BUTTONS>(props.modelValue);

const elementProperties = computed(() => props.modelValue.properties);

const inPdf = useIsInPdfMode();

const options = computed(() => {
  // return the radio button options from the element properties
  // if inPDF mode, only return the option that is currently selected
  if (inPdf.value) {
    return (
      elementProperties.value.radioButtonOptions?.filter(
        (option) => option.value === answer.value,
      ) || []
    );
  }
  return elementProperties.value.radioButtonOptions || [];
});
</script>

<template id="form-radio-buttons-component">
  <div class="RadioButtonsComponent">
    <slot name="label" :element="modelValue" />

    <el-radio-group
      v-if="
        elementProperties.displayFormat ===
        RadioButtonsDisplayFormat.BUTTON_STRIP
      "
      v-model="answer"
      :disabled="isReadOnly"
    >
      <el-radio-button
        v-for="{ label, value } in options"
        :key="value"
        :value="value"
        :label="label"
      >
        <span>{{ label }}</span>
        <span v-if="props.showOptionValues" class="component-question-code">
          {{ value }}</span
        >
      </el-radio-button>
    </el-radio-group>

    <el-radio-group
      v-if="
        elementProperties.displayFormat !==
        RadioButtonsDisplayFormat.BUTTON_STRIP
      "
      v-model="answer"
      :disabled="isReadOnly"
      class="normal"
    >
      <el-radio
        v-for="{ label, value } in options"
        :key="value"
        :value="value"
        :label="label"
      >
        <span>{{ label }}</span>
        <span v-if="props.showOptionValues" class="component-question-code">
          {{ value }}</span
        >
      </el-radio>
    </el-radio-group>
  </div>
</template>

<style lang="less">
.RadioButtonsComponent {
  display: flex;
  flex-direction: row;
  gap: 15px;
  align-items: flex-start;
  justify-content: space-between;

  .el-radio-group {
    flex-wrap: nowrap;
    &.normal {
      align-items: flex-start;
      flex-direction: column;
    }
    .el-radio-button__original-radio:disabled:checked
      + .el-radio-button__inner {
      background-color: #f2f2f2;
      color: #444142;
      font-weight: normal;
    }
    .el-radio-button__original-radio:disabled + .el-radio-button__inner {
      background-color: var(--el-color-primary-light-9);
      color: #cfcfcf;
      font-weight: normal;
    }
  }
  .pdfRadioButton {
    color: black;
    margin: 0 10px 0 0;
  }
}
body.forPdf .RadioButtonsComponent {
  .el-radio.is-disabled,
  .el-radio-button.is-disabled {
    opacity: 1;
  }

  .el-radio {
    margin-right: 10px;
    .el-radio__label {
      color: black;
      font-weight: normal;
    }

    .el-radio__input.is-disabled .el-radio__inner {
      border-color: black;
    }

    .el-radio__input.is-disabled.is-checked .el-radio__inner::after {
      background-color: black;
    }
  }

  .el-radio-button {
    .el-radio-button__inner {
      color: black;
    }

    .el-radio-button__original-radio:disabled + .el-radio-button__inner,
    .el-radio-button__original-radio:disabled:checked
      + .el-radio-button__inner {
      color: black;
      border-color: #444142;
      background-color: #f2f2f2;
      box-shadow: -1px 0 0 0 #444142;
    }

    &.is-active .el-radio-button__inner {
      color: black;
      border-color: #444142;
      background-color: #f2f2f2;
      box-shadow: -1px 0 0 0 #444142;
    }
  }
}
</style>
